import { FieldArray, getIn, useField, useFormikContext } from 'formik';
import { isEqual, uniqueId } from 'lodash';
import { Fragment, useEffect, useRef } from 'react';
import { SelectField } from '.';
import styled, { useTheme, css } from 'styled-components';
import getSymbolFromCurrency from 'currency-symbol-map';
import { FormLabel, ErrorMessageWrapper } from 'ui/forms';
import {
  KoobInputSelect,
  KoobInputNumber,
  KoobInputToggleField,
} from '@koob/ui';
import { useParams } from 'react-router-dom';
import { FormField } from 'ui/forms';
import {
  Dropdown,
  PopoverContainer,
  PopoverMenu,
  Stack,
  PopoverItemButton as RawPopoverItemButton,
  ButtonReset,
} from '@koob/margaret';
import { Text } from 'ui';
import VisuallyHidden from 'components/VisuallyHidden';
import { useTranslation } from 'react-i18next';
import { decodeId } from 'utils';
import { toDateString, dayjs } from '@koob/dates';
import { getHotelContracts } from 'api/node/contracts';
import {
  ContractStateFilterEnum,
  HotelContractStatusEnum,
  PromotionTypeEnum,
  PromotionStateEnum,
  PromotionKindEnum,
} from '@koob/enums';
import { useAsync } from 'react-async';
import PromotionPaxRestrictionField from './PromotionPaxRestrictionField';

const DropdownToggleWrapper = styled(Stack)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing(0.5)};
  box-shadow: 0 0 4px rgb(0 0 0 / 0.08);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  margin-bottom: ${({ theme }) => theme.spacing(0.25)};
  justify-content: space-between;
  align-items: center;
`;

const DropdownToggle = ({ children }) => {
  return (
    <DropdownToggleWrapper>
      {children}{' '}
      <i
        className="fa-light fa-chevron-down"
        style={{ fontSize: 16 }}
        aria-hidden="true"
      />
    </DropdownToggleWrapper>
  );
};

const DisplayListView = styled.ul`
  list-style: none;
  padding: ${({ theme }) => theme.spacing(0.5)}
    ${({ theme }) => theme.spacing(1)};
  margin: 0;
  border: solid 1px rgba(0, 0, 0, 0.12);
  border-radius: ${({ theme }) => theme.borderRadius.default};
  width: 100%;
  overflow-y: auto;
  max-height: 12ch;
`;

const DisplayListItem = styled.li`
  padding: ${({ theme }) => theme.spacing(0.25)} 0;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: center;
`;

const SelectionLabel = styled.span`
  text-transform: uppercase;
`;

const ClearAllButton = styled(ButtonReset).attrs({ type: 'button' })`
  color: ${({ theme }) => theme.secondary};
  text-decoration: underline;

  ${({ empty }) =>
    empty
      ? css`
          color: ${({ theme }) => theme.textLighter};
        `
      : null};
`;

const SummaryListHeader = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-self: stretch;
  align-items: center;
  justify-content: space-between;
  margin-block: ${({ theme }) => theme.spacing(1)}
    ${({ theme }) => theme.spacing(0.5)};
`;

const PopoverItemButton = styled(RawPopoverItemButton)`
  padding: ${({ theme }) => theme.spacing(0.5)}
    ${({ theme }) => theme.spacing()};

  color: ${({ theme }) => theme.textLight};

  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;
const FieldInputList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  color: ${({ theme }) => theme.text};
`;

const FieldInputItem = styled(Stack).attrs({ as: 'li' })`
  padding: ${({ theme }) => theme.spacing(1)} 0;
  color: ${({ theme }) => theme.textLight};
  &:only-child {
    border-bottom: none;
  }
`;

const AmenitiesField = ({ name, hotelAmenities, disabled }) => {
  const theme = useTheme();
  const [{ value }, amenitiesMeta, { setValue }] = useField(
    `${name}.amenities`,
  );
  const { t } = useTranslation('promotionForm');
  const { values, errors } = useFormikContext();
  const isOperational = values?.state === PromotionStateEnum.OPERATIONAL;
  const error = getIn(errors, `${name}.amenities`);
  const hasError = isOperational
    ? Boolean(error)
    : Boolean(amenitiesMeta.touched) && Boolean(error);

  const amenities = (hotelAmenities || []).map((node) => ({
    id: node?.id.toString(),
    displayName: node?.displayName,
  }));

  const handleClick = (selectedRoom) => {
    if (value?.length > 0) {
      if (value?.some(({ id }) => id === selectedRoom.id)) {
        setValue(value.filter(({ id }) => id !== selectedRoom.id));
      } else {
        setValue([...value, { ...selectedRoom }]);
      }
    } else {
      setValue([{ ...selectedRoom }]);
    }
  };

  const handleSelectAll = () => {
    setValue(amenities);
  };

  const handleRemoveAll = () => {
    setValue([]);
  };

  const handleRemoveById = (id) => {
    const nextState = value?.filter((item) => item.id !== id);
    setValue(nextState);
  };

  return (
    <Wrapper style={{ alignSelf: 'stretch' }}>
      <FormField>
        <FormLabel>
          {t('amenities.label')}
          {isOperational && <span className="ml-1 text-red-500">*</span>}
        </FormLabel>
        {!disabled && (
          <Stack direction="column" gutterSize={1} size="full" alignX="stretch">
            <Dropdown
              trigger={
                <DropdownToggle>
                  {t('amenities.searchAmenitiesToAdd')}
                </DropdownToggle>
              }
            >
              <PopoverContainer>
                <PopoverMenu>
                  {amenities?.map(({ id, displayName }) => (
                    <PopoverItemButton
                      key={id}
                      onClick={() => handleClick({ id, displayName })}
                      type="button"
                    >
                      <Stack
                        size="full"
                        alignX="space-between"
                        alignY="center"
                        gutterSize={0.5}
                      >
                        <Stack alignY="center" gutterSize={0.25}>
                          <Text type="body">
                            <SelectionLabel>{displayName}</SelectionLabel>
                          </Text>
                        </Stack>
                        {value?.some((bed) => bed.id === id) ? (
                          <i
                            className="fa-regular fa-circle-check"
                            style={{ fontSize: 24, color: theme.primary }}
                          />
                        ) : (
                          <i
                            className="fa-regular fa-circle-plus"
                            style={{ fontSize: 24, color: theme.textLight }}
                          />
                        )}
                      </Stack>
                    </PopoverItemButton>
                  ))}
                </PopoverMenu>
              </PopoverContainer>
            </Dropdown>
          </Stack>
        )}
      </FormField>
      {hasError && (
        <ErrorMessageWrapper style={{ marginTop: theme.spacing(0.25) }}>
          {t('errors:required')}
        </ErrorMessageWrapper>
      )}
      <SummaryListHeader>
        <span style={{ color: theme.textLight }}>
          {t('amenities.amenitiesSelected')} &middot; {value?.length ?? 0}
        </span>
        <Stack gutterSize={0.5}>
          <ClearAllButton
            empty={(value?.length ?? 0) === amenities?.length}
            onClick={handleSelectAll}
          >
            {t('applicableRooms.selectAll')}
          </ClearAllButton>
          <ClearAllButton
            empty={(value?.length ?? 0) === 0}
            onClick={handleRemoveAll}
          >
            {t('applicableRooms.deselectAll')}
          </ClearAllButton>
        </Stack>
      </SummaryListHeader>
      {value?.length > 0 && (
        <DisplayListView>
          {value?.map((item) => (
            <DisplayListItem key={item?.id}>
              <SelectionLabel>
                {item?.displayName?.toUpperCase()}
              </SelectionLabel>
              <ButtonReset onClick={() => handleRemoveById(item?.id)}>
                <VisuallyHidden>
                  {t('applicableRooms.deleteFromSelection', {
                    value: item?.displayName,
                  })}
                </VisuallyHidden>
                <i
                  className="fa-light fa-xmark"
                  style={{ fontSize: 24, color: theme.textLighter }}
                  aria-hidden="true"
                />
              </ButtonReset>
            </DisplayListItem>
          ))}
        </DisplayListView>
      )}
    </Wrapper>
  );
};

const ApplicableRoomsField = ({ name, hotelRooms, disabled }) => {
  const theme = useTheme();
  const [{ value }, roomsMeta, { setValue }] = useField(
    `${name}.applicableRooms`,
  );
  const { t } = useTranslation('promotionForm');
  const { values, errors } = useFormikContext();
  const isOperational = values?.state === PromotionStateEnum.OPERATIONAL;

  const error = getIn(errors, `${name}.applicableRooms`);
  const hasError = isOperational
    ? Boolean(error)
    : Boolean(roomsMeta.touched) && Boolean(error);

  const [, { value: type }] = useField(`${name}.type`);
  const [, { value: upgradeRoomValue }] = useField(`${name}.upgradedRoom`);

  const rooms = (
    type?.value !== 'upgrade_room'
      ? hotelRooms || []
      : (hotelRooms || []).filter(
          (node) => node?.id !== upgradeRoomValue?.value,
        )
  ).map((node) => ({
    id: node?.id,
    displayName: node?.displayName || node?.enDisplayName,
    count: node?.count,
  }));

  const handleClick = (selectedRoom) => {
    if (value?.length > 0) {
      if (value?.some(({ id }) => id === selectedRoom.id)) {
        setValue(value.filter(({ id }) => id !== selectedRoom.id));
      } else {
        setValue([...value, { ...selectedRoom }]);
      }
    } else {
      setValue([{ ...selectedRoom }]);
    }
  };

  const handleSelectAll = () => {
    setValue(rooms);
  };

  const handleRemoveAll = () => {
    setValue([]);
  };

  const handleRemoveById = (id) => {
    const nextState = value?.filter((item) => item.id !== id);
    setValue(nextState);
  };

  return (
    <Wrapper style={{ alignSelf: 'stretch' }}>
      <FormField>
        <FormLabel>
          {t('applicableRooms.applicableOnRooms')}
          {isOperational && <span className="ml-1 text-red-500">*</span>}
        </FormLabel>
        {!disabled && (
          <Stack direction="column" gutterSize={1} size="full" alignX="stretch">
            <Dropdown
              trigger={
                <DropdownToggle>
                  {t('applicableRooms.searchRoomsToAdd')}
                </DropdownToggle>
              }
            >
              <PopoverContainer>
                <PopoverMenu>
                  {rooms?.map(({ id, displayName, count }) => (
                    <PopoverItemButton
                      key={id}
                      onClick={() => handleClick({ id, displayName, count })}
                      type="button"
                    >
                      <Stack
                        size="full"
                        alignX="space-between"
                        alignY="center"
                        gutterSize={0.5}
                      >
                        <Stack alignY="center" gutterSize={0.25}>
                          <Text type="body">
                            <SelectionLabel>
                              {displayName} ({count})
                            </SelectionLabel>
                          </Text>
                        </Stack>
                        {value?.some((bed) => bed.id === id) ? (
                          <i
                            className="fa-regular fa-circle-check"
                            style={{ fontSize: 24, color: theme.primary }}
                          />
                        ) : (
                          <i
                            className="fa-regular fa-circle-plus"
                            style={{ fontSize: 24, color: theme.textLight }}
                          />
                        )}
                      </Stack>
                    </PopoverItemButton>
                  ))}
                </PopoverMenu>
              </PopoverContainer>
            </Dropdown>
          </Stack>
        )}
      </FormField>
      {hasError && (
        <ErrorMessageWrapper style={{ marginTop: theme.spacing(0.25) }}>
          {t('errors:required')}
        </ErrorMessageWrapper>
      )}
      <SummaryListHeader>
        <span style={{ color: theme.textLight }}>
          {t('applicableRooms.typesSelected')} &middot; {value?.length ?? 0}
        </span>
        <Stack gutterSize={0.5}>
          <ClearAllButton
            empty={(value?.length ?? 0) === rooms?.length}
            onClick={handleSelectAll}
          >
            {t('applicableRooms.selectAll')}
          </ClearAllButton>
          <ClearAllButton
            empty={(value?.length ?? 0) === 0}
            onClick={handleRemoveAll}
          >
            {t('applicableRooms.deselectAll')}
          </ClearAllButton>
        </Stack>
      </SummaryListHeader>
      {value?.length > 0 && (
        <DisplayListView>
          {value?.map((item) => (
            <DisplayListItem key={item?.id}>
              <SelectionLabel>
                {item?.displayName?.toUpperCase()} ({item?.count})
              </SelectionLabel>
              <ButtonReset onClick={() => handleRemoveById(item?.id)}>
                <VisuallyHidden>
                  {t('applicableRooms.deleteFromSelection', {
                    value: item?.displayName,
                  })}
                </VisuallyHidden>
                <i
                  className="fa-light fa-xmark"
                  style={{ fontSize: 24, color: theme.textLighter }}
                  aria-hidden="true"
                />
              </ButtonReset>
            </DisplayListItem>
          ))}
        </DisplayListView>
      )}
    </Wrapper>
  );
};

const UpgradeFromRoomField = ({ name, hotelRooms }) => {
  const { t } = useTranslation('promotionForm');
  const theme = useTheme();
  const { values } = useFormikContext();
  const isOperational = values?.state === PromotionStateEnum.OPERATIONAL;

  const [, { value, upgradedToId = value?.value }] = useField(
    `${name}.upgradedRoom.to`,
  );

  const rooms = (hotelRooms ?? [])
    .filter((item) => {
      return upgradedToId !== item?.id;
    })
    .map((item) => ({
      label: `${item.displayName} (${item.count})`,
      value: item.id,
    }));

  return (
    <Stack style={{ marginTop: theme.spacing(0.5) }}>
      <SelectField
        label={t('upgradedRoom.from')}
        options={rooms}
        name={`${name}.upgradedRoom.from`}
        required={isOperational}
        touchRequired={isOperational ? false : undefined}
      />
    </Stack>
  );
};

const UpgradeToRoomField = ({ name, hotelRooms }) => {
  const { t } = useTranslation('promotionForm');
  const theme = useTheme();
  const { values } = useFormikContext();
  const isOperational = values?.state === PromotionStateEnum.OPERATIONAL;

  const [, { value, upgradeRoomsId = value?.value }] = useField(
    `${name}.upgradedRoom.from`,
  );

  const rooms = (hotelRooms ?? [])
    .filter((item) => {
      return item.id !== upgradeRoomsId;
    })
    .map((item) => ({
      label: `${item.displayName} (${item.count})`,
      value: item.id,
    }));

  return (
    <Stack style={{ marginTop: theme.spacing(0.5) }}>
      <SelectField
        label={t('upgradedRoom.to')}
        options={rooms}
        name={`${name}.upgradedRoom.to`}
        required={isOperational}
        touchRequired={isOperational ? false : undefined}
      />
    </Stack>
  );
};

const Hint = styled.p`
  color: ${({ theme }) => theme.textLighter};
  margin: ${({ theme }) => theme.spacing(0.5)} 0 0;
  font-size: 0.9em;
`;

const Wrapper = styled(Stack).attrs({ direction: 'column' })`
  padding: ${({ theme }) => theme.spacing(0.5)} 0;
`;

export const BenefitsField = ({
  type,
  name,
  hotel,
  currency = undefined,
  isReadOnly = false,
}) => {
  const [, state, { setValue }] = useField(name);
  const [, valueMeta] = useField(`${name}.value`);
  const { values, errors } = useFormikContext();
  const isOperational = values?.state === PromotionStateEnum.OPERATIONAL;

  const error = getIn(errors, `${name}.value`);
  const hasError = isOperational
    ? Boolean(error)
    : Boolean(valueMeta.touched) && Boolean(error);

  const id = useRef(uniqueId(`${name}.benefits.`));
  const setBenefitsValue = (value) => {
    setValue({
      ...state.value,
      value,
    });
  };

  const items = {
    percent: (
      <NumberInputField
        kind="percent"
        id={id.current}
        value={state.value.value}
        onChange={setBenefitsValue}
        required={isOperational}
        hasError={hasError}
        error={error}
        disabled={isReadOnly}
      />
    ),
    amount: (
      <NumberInputField
        kind="amount"
        id={id.current}
        value={state.value.value}
        onChange={setBenefitsValue}
        currency={currency}
        required={isOperational}
        hasError={hasError}
        error={error}
        disabled={isReadOnly}
      />
    ),
    supplement_percent: (
      <NumberInputField
        kind="supplement_percent"
        id={id.current}
        value={state.value.value}
        onChange={setBenefitsValue}
        required={isOperational}
        hasError={hasError}
        error={error}
        disabled={isReadOnly}
      />
    ),
    supplement: (
      <NumberInputField
        kind="supplement"
        id={id.current}
        value={state.value.value}
        onChange={setBenefitsValue}
        currency={currency}
        required={isOperational}
        hasError={hasError}
        error={error}
        disabled={isReadOnly}
      />
    ),
    free_night: (
      <NumberInputField
        kind="free_night"
        id={id.current}
        value={state.value.value}
        onChange={setBenefitsValue}
        required={isOperational}
        hasError={hasError}
        error={error}
        disabled={isReadOnly}
      />
    ),
    amenity: <AmenitiesField name={name} hotelAmenities={hotel?.amenities} />,
  };

  return <>{type && items[type] ? <Wrapper>{items[type]}</Wrapper> : null}</>;
};

const PaxNode = ({ name, extraBed, type, currency }) => {
  const [, state, { setValue }] = useField(name);
  const [, valueMeta] = useField(`${name}.value`);
  const { values, errors } = useFormikContext();
  const isOperational = values?.state === PromotionStateEnum.OPERATIONAL;

  const error = getIn(errors, `${name}.value`);
  const hasError = isOperational
    ? Boolean(error)
    : Boolean(valueMeta.touched) && Boolean(error);

  const { t } = useTranslation('promotionForm');
  const setBenefitsValue = (value) => {
    setValue({
      ...state.value,
      age: extraBed.age,
      operand: extraBed.operand,
      value,
    });
  };

  return (
    <Fragment key={name}>
      <FieldInputItem>
        <div>
          {extraBed.operand === 'above' ? '>' : '<'}{' '}
          {t('yearsOld', {
            age: extraBed.age,
            count: extraBed.age === '' ? 0 : 1,
          })}
        </div>
      </FieldInputItem>
      <FieldInputItem>
        <NumberInputField
          kind={type}
          value={state.value?.value}
          id={`${name}.value`}
          currency={currency}
          onChange={setBenefitsValue}
          required={isOperational}
          hasError={hasError}
          error={error}
        />
      </FieldInputItem>
    </Fragment>
  );
};

const PaxField = ({ type, name, currency = undefined, contract }) => {
  const sortedContract = contract?.extraBeds.sort((a, b) => {
    if (a.age !== b.age) {
      return a.age - b.age;
    }
    if (a.operand === b.operand) return 0;
    return a.operand === 'below' ? -1 : 1;
  });
  if (!contract) {
    return <></>;
  }
  return (
    <>
      <FieldArray
        name={`${name}.benefitAgeGroups`}
        render={(_helpers) => {
          return (
            <>
              <FieldInputList>
                {sortedContract?.map((extraBed, index) => (
                  <PaxNode
                    key={index}
                    name={`${name}.benefitAgeGroups.${index}`}
                    extraBed={extraBed}
                    type={type}
                    currency={currency}
                  />
                ))}
              </FieldInputList>
            </>
          );
        }}
      />
    </>
  );
};

const NumberInputField = ({
  id,
  value,
  currency = 'eur',
  onChange = () => {},
  kind = 'percent',
  required,
  hasError,
  error,
  disabled,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('promotionForm');
  const kinds = {
    percent: {
      label: t('benefit.percent'),
      element: '%',
    },
    amount: {
      label: t('benefit.amount'),
      element: <Stack>{getSymbolFromCurrency(currency)}</Stack>,
    },
    supplement_percent: {
      label: t('benefit.supplement_percent'),
      element: '%',
    },
    supplement: {
      label: t('benefit.supplement'),
      element: <Stack>{getSymbolFromCurrency(currency)}</Stack>,
    },
    free_night: {
      label: t('benefit.free_night'),
      element: (
        <i
          className="fa-light fa-moon"
          style={{ fontSize: 20, color: theme.textLighter }}
          aria-hidden="true"
        />
      ),
    },
  };

  return (
    <div className="flex flex-col w-full">
      <FormLabel htmlFor={id}>
        {kinds?.[kind]?.label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </FormLabel>
      <Stack direction="row" alignY="center" style={{ width: '100%' }}>
        <KoobInputNumber
          style={{ flex: 1 }}
          id={id}
          min={0}
          max={kind === 'percent' ? 100 : undefined}
          integer={kind === 'free_night'}
          error={hasError}
          value={value}
          onValueChange={(next) => onChange(next ?? '')}
          disabled={disabled}
        />
        {kinds?.[kind]?.element && (
          <Stack
            style={{
              paddingBlock: theme.spacing(0.25),
              paddingInline: theme.spacing(0.5),
              color: theme.textLight,
            }}
          >
            {kinds?.[kind]?.element}
          </Stack>
        )}
      </Stack>
      {hasError && (
        <ErrorMessageWrapper style={{ marginTop: theme.spacing(0.25) }}>
          {typeof error === 'string' ? error : t('errors:required')}
        </ErrorMessageWrapper>
      )}
      {kind === 'amount' && <Hint>{t('benefit.currencyHint')}</Hint>}
    </div>
  );
};

const isStayFlexibilityKind = (kind) =>
  kind === PromotionKindEnum.STAY_FLEXIBILITY;

const PromotionTypeField = ({ name }) => {
  const { t } = useTranslation('promotionForm');
  const { id, promotionId } = useParams();
  const { values, initialValues } = useFormikContext();
  const isOperational = values?.state === PromotionStateEnum.OPERATIONAL;
  const promoId = id || promotionId;
  const initialType =
    getIn(initialValues, `${name}.type`) ||
    getIn(initialValues, `${name}.kind`);
  const canEditType = !promoId || !initialType;
  const isStayFlexibilityDeal = isStayFlexibilityKind(values?.kind);
  // KD-3496 — an early check-in / late check-out is priced off the room it
  // extends, so the only type it may be sold under is the percentage
  // supplement: a flat amount would not follow the room's season or category.
  // The API drops the stay-flexibility code on any other benefit type, so
  // offering one here would silently produce an ordinary supplement.
  const parametersTypesOptions = isStayFlexibilityDeal
    ? [
        {
          label: t('promotionTypes.supplement_percent'),
          value: 'supplement_percent',
        },
        {
          label: t('promotionTypes.supplement'),
          value: 'supplement',
        },
      ]
    : [
        {
          label: t('promotionTypes.amount'),
          value: 'amount',
        },
        {
          label: t('promotionTypes.percent'),
          value: 'percent',
        },
        {
          label: t('promotionTypes.free_night'),
          value: 'free_night',
        },
        {
          label: t('promotionTypes.amenities'),
          value: 'amenity',
        },
        {
          label: t('promotionTypes.upgrade_room'),
          value: 'upgrade_room',
        },
        {
          label: t('promotionTypes.supplement'),
          value: 'supplement',
        },
        {
          label: t('promotionTypes.supplement_percent'),
          value: 'supplement_percent',
        },
      ];

  return (
    <Wrapper>
      <SelectField
        disabled={!canEditType}
        name={`${name}.type`}
        options={parametersTypesOptions}
        label={t('promotionTypes.label')}
        testId="type"
        required={isOperational}
        touchRequired={isOperational ? false : undefined}
      />
    </Wrapper>
  );
};

const PromotionParamsField = ({
  name,
  disabled,
  hotel,
  setContract,
  contract,
}) => {
  const [, state, { setValue }] = useField(name);
  const { t } = useTranslation('promotionForm');
  const isUpgradeRoomPromotion = state?.value?.type?.value === 'upgrade_room';
  const { values, validateField, setFieldValue } = useFormikContext();

  const periods = getIn(values, 'periods');
  const paxRestriction = getIn(values, `${name}.paxRestriction`);

  const { hotelId } = useParams();

  // Overall range covered by the periods, ignoring the rows not filled in yet.
  const datedPeriods = (periods ?? []).filter(
    (period) => period.startAt && period.endAt,
  );

  const startDate =
    datedPeriods.length > 0
      ? datedPeriods.reduce((prev, curr) =>
          dayjs(prev.startAt).isBefore(dayjs(curr.startAt)) ? prev : curr,
        ).startAt
      : undefined;

  const endDate =
    datedPeriods.length > 0
      ? datedPeriods.reduce((prev, curr) =>
          dayjs(prev.endAt).isAfter(dayjs(curr.endAt)) ? prev : curr,
        ).endAt
      : undefined;

  const newStartDate = dayjs.max(dayjs(), dayjs(startDate));

  const hotelRooms = (hotel?.rooms || []).map((room) => {
    return {
      id: decodeId(room.id).toString(),
      count: room.count,
      displayName: room.displayName,
    };
  });

  const { data } = useAsync({
    promiseFn: getHotelContracts,
    hotelId,
    filters: {
      status: HotelContractStatusEnum.PUBLISHED,
      contractStateFilter: ContractStateFilterEnum.AVAILABLE,
      startAt: toDateString(newStartDate),
      endAt: endDate,
    },
    skip: !hotelId || !startDate || !endDate,
    watchFn: (props, prevProps) => {
      return !isEqual(props.filters, prevProps.filters);
    },
  });
  const contracts = data?.data;

  useEffect(() => {
    validateField(name);
  }, [contract, name, validateField, values?.state]);

  if (contracts) {
    if (contracts.length === 1) {
      setContract(contracts[0]);
      if (
        contract &&
        state &&
        contracts.extraBeds?.length === 0 &&
        state.value.benefitAgeGroups &&
        state.value.benefitAgeGroups.length !== 0
      ) {
        setValue({ ...state.value, benefitAgeGroups: [] });
      }
    } else {
      setContract(undefined);
      if (
        state &&
        state.value.benefitAgeGroups &&
        state.value.benefitAgeGroups.length !== 0
      ) {
        setValue({ ...state.value, benefitAgeGroups: [] });
      }
    }
  }
  const promotionType = state?.value?.type?.value;

  const isAmountOrPercentPromotion =
    Object.values(PromotionTypeEnum).includes(promotionType);

  const unitOptions = [
    {
      label: t('benefit.unit.per_unit'),
      value: 'per_unit',
    },
    {
      label: t('benefit.unit.per_night'),
      value: 'per_night',
    },
  ];

  const isStayFlexibilityDeal = isStayFlexibilityKind(values?.kind);

  const promoPerRoomChecked = state?.value?.promoPerRoom;
  const extraBedsOnlyChecked = Boolean(state?.value?.extraBedsOnly);

  // Neither a Stay flexibility deal nor a pax restriction exposes the
  // extra-beds-only toggle: reset it when either comes after it was checked, so
  // a hidden value is not sent. A per-pax offer then stops applying to the
  // extra beds, which it only did through extraBedsOnly.
  const hidesExtraBedsOnly =
    isStayFlexibilityDeal || Boolean(paxRestriction?.enabled);
  useEffect(() => {
    if (!hidesExtraBedsOnly || !extraBedsOnlyChecked) {
      return;
    }
    setFieldValue(`${name}.extraBedsOnly`, false);
    if (!promoPerRoomChecked) {
      setFieldValue(`${name}.includeExtraBeds`, false);
    }
  }, [
    hidesExtraBedsOnly,
    extraBedsOnlyChecked,
    promoPerRoomChecked,
    name,
    setFieldValue,
  ]);

  // Stay flexibility deals always apply per room, that toggle is forced and
  // locked. includeExtraBeds stays editable: an early check-in / late check-out
  // may or may not be charged on the extra beds of the room.
  useEffect(() => {
    if (!isStayFlexibilityDeal) {
      return;
    }
    if (promoPerRoomChecked !== true) {
      setFieldValue(`${name}.promoPerRoom`, true);
    }
  }, [isStayFlexibilityDeal, promoPerRoomChecked, name, setFieldValue]);

  return (
    <>
      <PromotionTypeField name={name} />
      <div className="my-2 w-full">
        <PromotionPaxRestrictionField name={`${name}.paxRestriction`} />
      </div>
      {isAmountOrPercentPromotion && !paxRestriction?.enabled && (
        <div className="flex flex-col space-y-2 w-full">
          <KoobInputToggleField
            name={`${name}.promoPerRoom`}
            label={t('promoPerRoom')}
            testId="promoPerRoom"
            disabled={isStayFlexibilityDeal}
            onChange={(value) => {
              setValue({
                ...state.value,
                promoPerRoom: value,
                // A per-pax offer does not apply to the extra beds, unless it
                // targets them only (then it applies to nothing else).
                includeExtraBeds: !value
                  ? Boolean(state.value.extraBedsOnly)
                  : state.value.includeExtraBeds,
              });
            }}
          />

          <KoobInputToggleField
            name={`${name}.includeExtraBeds`}
            label={t('includeExtraBeds')}
            testId="includeExtraBeds"
            disabled={!promoPerRoomChecked || extraBedsOnlyChecked}
            resetOnDisable={!extraBedsOnlyChecked}
          />

          {!isStayFlexibilityDeal && (
            <KoobInputToggleField
              name={`${name}.extraBedsOnly`}
              label={t('extraBedsOnly')}
              testId="extraBedsOnly"
              onChange={(value) => {
                setValue({
                  ...state.value,
                  extraBedsOnly: value,
                  // Targeting the extra beds only implies applying to them; once
                  // unchecked, a per-pax offer no longer applies to them.
                  includeExtraBeds: value
                    ? true
                    : Boolean(state.value.promoPerRoom) &&
                      state.value.includeExtraBeds,
                });
              }}
            />
          )}
        </div>
      )}
      {promotionType === PromotionTypeEnum.SUPPLEMENT && (
        <div className="flex flex-col gap-y-2 w-full mt-2">
          <FormLabel>{t('benefit.unit.label')}</FormLabel>
          <KoobInputSelect
            options={unitOptions}
            onValueChange={(value) => {
              setValue({
                ...state.value,
                unit: value,
              });
            }}
            value={state.value.unit ?? 'per_night'}
          />
        </div>
      )}
      {/* A stay flexibility deal always applies per room, so it never lands on
          the per-pax breakdown: it gets the single supplement amount input. */}
      {isAmountOrPercentPromotion &&
      !state?.value?.promoPerRoom &&
      contract?.extraBeds?.length > 0 ? (
        <PaxField
          name={name}
          currency={hotel?.currency}
          contract={contract}
          type={state?.value?.type?.value}
        />
      ) : (
        <BenefitsField
          currency={hotel?.currency}
          name={name}
          type={state?.value?.type?.value}
          hotel={hotel}
        />
      )}

      {isUpgradeRoomPromotion ? (
        <>
          <UpgradeFromRoomField
            name={name}
            hotelRooms={hotelRooms}
            disabled={disabled}
          />
          <UpgradeToRoomField
            name={name}
            hotelRooms={hotelRooms}
            disabled={disabled}
          />
        </>
      ) : (
        <ApplicableRoomsField
          name={name}
          hotelRooms={hotelRooms}
          disabled={disabled}
        />
      )}
    </>
  );
};

export default PromotionParamsField;
